// frontend/src/components/MatchStatistics/MatchStatistics.js
// Purpose: Display summary statistics of part matching without triggering unnecessary API calls.
// Desired outcome: This component reads and presents statistical data from Redux about part matching confidence levels and counts, ensuring no unnecessary re-fetching occurs.

import React from 'react';
import './MatchStatistics.css';
import { useSelector } from 'react-redux';
import { selectBootstrapInProgress, selectBootstrapComplete } from '../../state/analysis/analysisSlice';

// Wrapping in React.memo to prevent unnecessary re-renders
const MatchStatistics = React.memo(({ partMatches, confidenceThreshold }) => {
  const bootstrapInProgress = useSelector(selectBootstrapInProgress);
  const bootstrapComplete = useSelector(selectBootstrapComplete);

  if (bootstrapInProgress) {
    return <div className="loading-indicator">Initializing analysis...</div>;
  }

  if (!bootstrapComplete || !partMatches || partMatches.length === 0) {
    return <div className="match-stats empty">No matching statistics available.</div>;
  }

  // Calculate statistics
  const totalParts = partMatches.length;
  const categorizedParts = partMatches.filter((match) => match.matchedCategory !== 'Uncategorized').length;
  const uncategorizedParts = totalParts - categorizedParts;
  const categorizedPercent = Math.round((categorizedParts / totalParts) * 100);

  // Compute above/below threshold stats
  const aboveThreshold = partMatches.filter((match) => match.confidenceScore >= confidenceThreshold).length;
  const belowThreshold = totalParts - aboveThreshold;
  const aboveThresholdPercent = Math.round((aboveThreshold / totalParts) * 100);

  // Count occurrences of each category
  const categoryCounts = partMatches.reduce((acc, match) => {
    acc[match.matchedCategory] = (acc[match.matchedCategory] || 0) + 1;
    return acc;
  }, {});

  // Sort categories by count (descending) and limit to top 5
  const sortedCategories = Object.entries(categoryCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 5);

  return (
    <div className="match-stats">
      <h3>Matching Statistics</h3>

      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-value">{totalParts}</div>
          <div className="stat-label">Total Parts</div>
        </div>

        <div className="stat-item">
          <div className="stat-value">{categorizedPercent}%</div>
          <div className="stat-label">Categorized ({categorizedParts} parts)</div>
        </div>

        <div className="stat-item">
          <div className="stat-value">{aboveThresholdPercent}%</div>
          <div className="stat-label">Above Threshold ({aboveThreshold} parts)</div>
        </div>

        <div className="stat-item">
          <div className="stat-value">{uncategorizedParts}</div>
          <div className="stat-label">Uncategorized Parts</div>
        </div>
      </div>

      {sortedCategories.length > 0 && (
        <div className="category-distribution">
          <h4>Top Categories</h4>
          <div className="category-bars">
            {sortedCategories.map(([category, count]) => {
              const percentage = Math.round((count / totalParts) * 100);
              return (
                <div key={category} className="category-bar-item">
                  <div className="category-label">
                    <span>{category}</span>
                    <span>{count} ({percentage}%)</span>
                  </div>
                  <div className="category-bar">
                    <div
                      className="category-bar-fill"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

// Export component for reuse in the application
export default MatchStatistics;